<i18n src="./resources/locales.json"></i18n>

<template>
  <section ref="tabsContainer" class="ecs-interaction-tabs" :dark="$vuetify.theme.dark">
    <v-tabs v-show="multipleElements" ref="tabs" v-model="tab" no-animation>
      <v-tab
        v-for="element in elements"
        :key="'tab-' + element.elementId"
        class="element-tab"
        :class="{ 'blink lighten': getUnreadMessagesNotificationsForElement(element.elementId).length }"
        active-class="active-tab"
        :ripple="{ class: 'ripple-color' }"
        @click="onTabClick(element.elementId)"
      >
        <div class="tab-avatar">
          <v-progress-circular
            class="circular-progress"
            :indeterminate="element.isActivating"
            :size="24"
            :width="2"
            color="var(--v-primary-base)"
          >
            <transition mode="out-in">
              <v-avatar v-if="getTabIcon(element)" :size="18" color="transparent">
                <v-icon class="state-icon">{{ getTabIcon(element) }}</v-icon>
              </v-avatar>
              <EcsAvatar
                v-else
                :agent-id="isAgent(element.receptor) ? element.receptor.id : null"
                :contact-id="isAgent(element.receptor) ? null : element.receptor && element.receptor.id"
                :alt="getTabLabel(element)"
                :size="18"
                no-animation
              />
            </transition>
          </v-progress-circular>
        </div>

        <div class="tab-label">
          {{ getTabLabel(element) }}
          <div v-show="getUnreadMessagesNotificationsForElement(element.elementId).length" class="notification-badge">
            {{ getUnreadMessagesNotificationsForElement(element.elementId).length }}
          </div>
        </div>

        <div class="tab-actions">
          <v-btn
            v-if="!element.isClosed && !element.isAbandoned"
            icon
            small
            class="close-element-btn"
            @click="closeElement(element)"
          >
            <v-icon class="ecs-contact-item-action-icon">
              mdi-close
            </v-icon>
          </v-btn>
          <v-btn
            v-else-if="!isHistoricalInteraction"
            icon
            small
            class="close-element-btn"
            @click="deleteElement(element)"
          >
            <v-icon class="ecs-contact-item-action-icon">
              mdi-delete
            </v-icon>
          </v-btn>
        </div>
      </v-tab>
      <v-tab v-if="isEmpty">Empty</v-tab>
    </v-tabs>
    <div v-show="showAnimation" class="inset-fade-effect" :dark="$vuetify.theme.dark" />
    <v-tabs-items ref="tabItems" v-model="tab">
      <v-tab-item
        v-for="element in elements"
        :key="'tab-content-' + element.elementId"
        @click="clearUnreadMessagesNotifications(element.elementId)"
      >
        <div class="tab-content">
          <slot name="eachTabContent" :element="element" />
        </div>
      </v-tab-item>
      <v-tab-item v-if="interaction.isChat && interaction.isHistoric && historyChatMessages.length">
        <EcsConversationMessages :messages="historyChatMessages" disabled :is-sms="false" />
      </v-tab-item>
      <v-tab-item
        v-else-if="interaction.isChat && interaction.isHistoric && historyChatMessages.length === 0"
        class="no-content"
      >
        <h1 v-once>{{ $t('noMessages.title') }}</h1>
        <p v-once>{{ $t('noMessages.description') }}</p>
      </v-tab-item>
      <v-tab-item v-else-if="isEmpty && !historyChatMessages.length" class="no-content no-historic">
        <h1 v-once>{{ $t('noElements.title') }}</h1>
        <p v-once>{{ $t('noElements.description') }}</p>
      </v-tab-item>
    </v-tabs-items>
  </section>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import { ELEMENT_STATE } from '@/constants/element/constants';
import EcsAvatar from '@/components/ecs-base-interaction-components/ecs-avatar/EcsAvatar';
import EcsConversationMessages from '@/components/ecs-conversation/ecs-conversation-messages/EcsConversationMessages';
import InteractionService from '@/services/interactionService';
import { CONTACT_TYPE } from '@/constants/contactConstants';
import AgentService from '@/services/agentService';

export default {
  name: 'EcsInteractionTabs',

  components: {
    EcsAvatar,
    EcsConversationMessages
  },

  props: {
    interaction: {
      type: Object,
      default: () => {
        return {};
      }
    }
  },

  data() {
    return {
      tab: null,
      historyChatMessages: []
    };
  },

  computed: {
    ...mapGetters('AGENT', ['loggedInAgent']),

    ...mapGetters('CONTACTS', ['getInternalBusinessContact', 'getContactV2']),

    ...mapGetters('ELEMENTS', ['getElementsForInteraction']),

    ...mapGetters('INTERACTIONS', ['getHistoricalInteraction', 'getInteraction']),

    ...mapGetters('NOTIFICATIONS', ['getUnreadMessagesNotificationsForElement']),

    interactionId() {
      return this.interaction.interactionId;
    },

    elements() {
      const allElements = this.getElementsForInteraction(this.interaction) || [];
      const el = this.removeDuplicatedElements(allElements);
      const unclosedElements = el.filter(element => !element.isClosed);

      // Return only unclosed elements else all of them because the interaction was ended
      return unclosedElements.length ? unclosedElements : el;
    },

    isHistoricalInteraction() {
      return !!this.getHistoricalInteraction(this.interactionId);
    },

    participantNames() {
      return (this.getElementsForInteraction(this.element.interactionId) || []).map(
        element => element.receptor.name.split(' ')[0]
      );
    },

    isEmpty() {
      return this.elements.length === 0;
    },

    multipleElements() {
      return this.elements.length > 1;
    },

    showAnimation() {
      return this.elements.length === 1 && !this.interaction.isChat;
    }
  },

  watch: {
    /**
     * Detects if new elements were added and sets focus on it's corresponding tab.
     **/
    elements(oldVal, newVal) {
      if (oldVal.length > newVal.length) {
        this.tab = this.elements.length - 1;
      }
    }
  },

  created() {
    if (this.isEmpty && this.interaction.isChat && this.interaction.isHistoric) {
      this.loadFallbackConversationHistory();
    }
  },

  methods: {
    ...mapActions('ELEMENTS', ['removeElement']),

    ...mapActions('NOTIFICATIONS', ['clearUnreadMessagesNotifications']),

    ...mapActions('WINDOWS', ['setFocusedElementId']),

    removeDuplicatedElements(items) {
      const elementsMap = {};
      items.forEach(element => {
        elementsMap[element.elementId] = element;
      });
      return Object.values(elementsMap);
    },

    async loadFallbackConversationHistory() {
      const response = await AgentService.getConversationHistory(this.interactionId);
      const rawMessages = (response.data || []).filter(message => message.content).reverse();
      const currentUser = { id: this.loggedInAgent.id, name: this.loggedInAgent.name };

      this.historyChatMessages = rawMessages.map(({ originatorName, content, timestamp }) => {
        const isRemotePartyMessage = originatorName === this.interaction.routing.remoteParty;
        const remoteParty = { id: this.interaction.crm.contactId, name: originatorName };
        const originator = isRemotePartyMessage ? remoteParty : currentUser;

        return { originator, content, timestamp };
      });
    },

    closeElement({ elementId }) {
      InteractionService.closeElement(this.interactionId, elementId);
      this.$emit('element-closed', elementId);
    },

    deleteElement({ elementId }) {
      this.removeElement({ elementId });
      this.$emit('element-removed', elementId);
    },

    getTabLabel(element) {
      const receptor = element.receptors.length
        ? element.receptors[0].name || element.receptors[0].endPoint
        : this.$t('unknown');
      const receptorOneName = (receptor || '').split(' ')[0];
      if (element.isConferenced) {
        return `${receptorOneName} - ${this.$t('conference')}`;
      }
      return receptor;
    },

    getTabIcon(element) {
      let icon = null;
      switch (element.elementStateId) {
        case ELEMENT_STATE.diverted:
        case ELEMENT_STATE.conferencedDiverted:
          icon = 'mdi-pause-circle';
          break;
        case ELEMENT_STATE.conferenced:
          icon = 'mdi-account-group';
          break;
        default:
          break;
      }
      return icon;
    },

    isAgent(receptor) {
      return (
        receptor &&
        receptor.type &&
        [CONTACT_TYPE.agent, CONTACT_TYPE.supervisor, CONTACT_TYPE.enterprise].includes(receptor.type)
      );
    },

    async updateTabsSlider() {
      await this.$nextTick();
      this.$refs.tabs.callSlider();
    },

    onTabClick(elementId) {
      this.clearUnreadMessagesNotifications(elementId);
      this.setFocusedElementId(elementId);
    }
  }
};
</script>

<style src="./scoped.css" scoped />
<style src="./global.css" />
